/**
 * REEDOS Knowledge Base — article page (`/kb/article/:slug`).
 *
 * Composes every article-scoped concern:
 *   · Breadcrumbs                         — trail: product > section > title
 *   · Article header (title + badges)     — version, status, revision, language
 *   · Section renderer                     — auto_generated + prose sections
 *   · Prev / Next navigation               — derived from the sidebar order
 *   · Related articles                     — from frontmatter.related_articles[]
 *   · Supplements                          — SME-editable notes
 *   · Print header                         — hidden on screen, shown on print
 *   · Analytics                            — fires kbRecordView on mount and unmount
 *
 * All server responses are already role-filtered; if the caller is
 * NOT permitted, the backend returns 403 → we render an access-denied
 * card rather than crashing.
 */
import { Link, useOutletContext, useParams } from "react-router-dom";
import { useEffect } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, FileWarning, Printer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useKbArticle, kbRecordView } from "@/lib/kb";

// ─────────────────────────────────────────────────────────────────────
// Small badge-style pill for the article header
// ─────────────────────────────────────────────────────────────────────
function HeaderPill({ tone = "neutral", children, testId }) {
    const cls =
        tone === "danger"
            ? "bg-destructive/15 text-destructive"
            : tone === "warn"
            ? "bg-amber-100 text-amber-800"
            : tone === "info"
            ? "bg-sky-100 text-sky-800"
            : "bg-muted text-muted-foreground";
    return (
        <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
            data-testid={testId}
        >
            {children}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section renderer
// ─────────────────────────────────────────────────────────────────────
function ArticleSection({ section }) {
    const isAuto = section.kind === "auto_generated";
    return (
        <section
            className="mb-6"
            data-testid={`kb-section-${section.heading.replace(/\s+/g, "-").toLowerCase()}`}
        >
            <div className="flex items-center gap-2 mb-2">
                <h2 className="text-xl font-semibold">{section.heading}</h2>
                {isAuto && (
                    <span
                        className="kb-section-badge kb-section-badge-auto"
                        title="Auto-generated from code introspection"
                    >
                        Auto-generated
                    </span>
                )}
            </div>
            <div
                className="kb-prose"
                dangerouslySetInnerHTML={{ __html: section.body_html || "" }}
            />
        </section>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────
export default function KBArticlePage() {
    const { "*": splat } = useParams();
    // React Router v6 splat capture: the slug may contain slashes.
    const slug = splat;
    const { flatSlugs, productCodeToName } = useOutletContext();
    const { data, loading, error } = useKbArticle(slug);

    // Fire view event on mount; also fire an unmount event with dwell_ms.
    useEffect(() => {
        if (!slug) return undefined;
        kbRecordView({ article_slug: slug, source: "sidebar" });
        const start = Date.now();
        return () => {
            const dwell = Date.now() - start;
            kbRecordView({ article_slug: slug, source: "sidebar", dwell_ms: dwell });
        };
    }, [slug]);

    // Error branches
    if (loading) {
        return <div className="p-4 text-muted-foreground" data-testid="kb-article-loading">Loading…</div>;
    }
    if (error) {
        const status = error?.response?.status;
        if (status === 403) {
            return (
                <div className="max-w-2xl border border-destructive/40 rounded-md p-6 bg-destructive/5" data-testid="kb-article-forbidden">
                    <FileWarning className="h-6 w-6 text-destructive mb-2" />
                    <h1 className="text-xl font-semibold">You don&apos;t have access to this article.</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {error?.response?.data?.detail || "Ask your administrator to grant the required permissions."}
                    </p>
                </div>
            );
        }
        if (status === 404) {
            return (
                <div className="max-w-2xl border border-border rounded-md p-6 bg-muted/40" data-testid="kb-article-not-found">
                    <FileWarning className="h-6 w-6 text-muted-foreground mb-2" />
                    <h1 className="text-xl font-semibold">Article not found.</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        The slug <code>{slug}</code> doesn&apos;t map to any published article.
                    </p>
                    <Link to="/kb" className="text-primary text-sm hover:underline mt-3 inline-block">
                        ← Back to the Knowledge Base
                    </Link>
                </div>
            );
        }
        return (
            <div className="p-4 text-destructive" data-testid="kb-article-error">
                Failed to load the article.
            </div>
        );
    }

    const article = data?.article;
    const supplements = data?.supplements || [];
    if (!article) {
        return null;
    }

    // Prev / next derivation from the flat sidebar order.
    const currentIdx = flatSlugs.findIndex((a) => a.slug === article.slug);
    const prev = currentIdx > 0 ? flatSlugs[currentIdx - 1] : null;
    const next = currentIdx >= 0 && currentIdx < flatSlugs.length - 1 ? flatSlugs[currentIdx + 1] : null;

    const productName = productCodeToName?.[article.product] || article.product;

    return (
        <article className="max-w-3xl kb-article-body" data-testid="kb-article-page">
            {/* Print-only header, hidden on screen. */}
            <header className="kb-print-header">
                <div style={{ fontSize: "1.25rem", fontWeight: 600 }}>{article.title}</div>
                <div style={{ fontSize: "0.85rem", color: "#555" }}>
                    {productName} · {article.slug} · v{article.version?.introduced}
                </div>
            </header>

            {/* Breadcrumb + print button */}
            <nav aria-label="Breadcrumb" className="mb-3 kb-print-hidden" data-testid="kb-breadcrumbs">
                <ol className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
                    <li>
                        <Link to="/kb" className="hover:text-foreground">
                            Knowledge Base
                        </Link>
                    </li>
                    <li>›</li>
                    <li className="text-foreground">{productName}</li>
                    {article.navigation?.section && (
                        <>
                            <li>›</li>
                            <li>{article.navigation.section}</li>
                        </>
                    )}
                    {article.navigation?.subsection && (
                        <>
                            <li>›</li>
                            <li>{article.navigation.subsection}</li>
                        </>
                    )}
                    <li className="ml-auto">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.print()}
                            data-testid="kb-article-print"
                            aria-label="Print this article"
                        >
                            <Printer className="h-4 w-4" />
                        </Button>
                    </li>
                </ol>
            </nav>

            {/* Header */}
            <header className="mb-5">
                <h1 className="text-3xl font-semibold text-foreground" data-testid="kb-article-title">
                    {article.title}
                </h1>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {/* KB Phase A · Wave-1 — article-kind pill. */}
                    {article.kind && article.kind !== "core" && (
                        <HeaderPill
                            tone={article.kind === "troubleshooting" ? "warn" : "info"}
                            testId={`kb-article-kind-${article.kind}`}
                        >
                            {article.kind === "field_reference" && "Field Reference"}
                            {article.kind === "troubleshooting" && "Troubleshooting"}
                            {article.kind === "concept" && "Concept"}
                            {article.kind === "core" && "Core"}
                        </HeaderPill>
                    )}
                    {/* KB Phase A · A8 — maturity pill.
                        DRAFT / REVIEW are only reachable by KB
                        reviewers (backend enforces via kb_visibility);
                        the pill makes the state unmistakable. */}
                    {article.maturity && article.maturity !== "published" && (
                        <HeaderPill
                            tone={article.maturity === "draft" ? "warn" : "warn"}
                            testId={`kb-article-maturity-${article.maturity}`}
                        >
                            {article.maturity.toUpperCase()}
                        </HeaderPill>
                    )}
                    {article.version?.status && article.version.status !== "active" && (
                        <HeaderPill
                            tone={article.version.status === "removed" ? "danger" : "warn"}
                            testId={`kb-article-status-${article.version.status}`}
                        >
                            {article.version.status.toUpperCase()}
                        </HeaderPill>
                    )}
                    {article.version?.introduced && (
                        <HeaderPill testId="kb-article-version">
                            App v{article.version.introduced}
                            {article.version.last_changed &&
                                article.version.last_changed !== article.version.introduced &&
                                ` · updated v${article.version.last_changed}`}
                        </HeaderPill>
                    )}
                    {article.revision > 1 && (
                        <HeaderPill testId="kb-article-revision">
                            rev {article.revision}
                        </HeaderPill>
                    )}
                    {article.language && article.language !== "en" && (
                        <HeaderPill testId="kb-article-language">
                            {article.language}
                        </HeaderPill>
                    )}
                    {(article.tags || []).map((t) => (
                        <Badge key={t} variant="secondary" data-testid={`kb-article-tag-${t}`}>
                            #{t}
                        </Badge>
                    ))}
                </div>
                {/* KB Phase A · Wave-1 — editorial review trail. */}
                {(article.last_reviewed_by || article.last_reviewed_at) && (
                    <div
                        className="mt-2 text-xs text-muted-foreground"
                        data-testid="kb-article-review-trail"
                    >
                        Last reviewed
                        {article.last_reviewed_by && (
                            <> by <span className="font-medium">{article.last_reviewed_by}</span></>
                        )}
                        {article.last_reviewed_at && (
                            <> on <time dateTime={article.last_reviewed_at}>{article.last_reviewed_at}</time></>
                        )}
                    </div>
                )}
            </header>

            {/* Sections */}
            {(article.sections || []).map((s, i) => (
                <ArticleSection key={`${s.heading}-${i}`} section={s} />
            ))}

            {/* Supplements */}
            {supplements.length > 0 && (
                <aside
                    className="kb-supplement-panel mt-8 border-l-4 border-primary/50 bg-primary/5 p-4 rounded-r-md"
                    data-testid="kb-article-supplements"
                >
                    <h3 className="text-sm font-semibold text-primary mb-2">
                        Notes from your organisation
                    </h3>
                    {supplements.map((s) => (
                        <div
                            key={s.id}
                            className="kb-prose text-sm"
                            dangerouslySetInnerHTML={{ __html: s.content_html || "" }}
                        />
                    ))}
                </aside>
            )}

            {/* Related articles */}
            {(article.related_articles || []).length > 0 && (
                <aside
                    className="kb-related-panel mt-8"
                    data-testid="kb-article-related"
                    aria-label="Related articles"
                >
                    <h3 className="text-sm font-semibold mb-2">Related articles</h3>
                    <ul className="space-y-1">
                        {article.related_articles.map((s) => (
                            <li key={s}>
                                <Link
                                    to={`/kb/article/${s}`}
                                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                                    data-testid={`kb-related-${s}`}
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    {s}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </aside>
            )}

            {/* Prev / Next */}
            {(prev || next) && (
                <nav
                    className="kb-prev-next mt-10 border-t border-border pt-4 flex items-center justify-between gap-4"
                    aria-label="Previous and next articles"
                    data-testid="kb-prev-next"
                >
                    {prev ? (
                        <Link
                            to={`/kb/article/${prev.slug}`}
                            className="group flex-1 border border-border rounded-md p-3 hover:border-primary hover:bg-muted/40"
                            data-testid="kb-prev-link"
                        >
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <ChevronLeft className="h-3.5 w-3.5" />
                                Previous
                            </div>
                            <div className="text-sm font-medium mt-1 group-hover:text-primary truncate">
                                {prev.title}
                            </div>
                        </Link>
                    ) : (
                        <span className="flex-1" />
                    )}
                    {next ? (
                        <Link
                            to={`/kb/article/${next.slug}`}
                            className="group flex-1 border border-border rounded-md p-3 text-right hover:border-primary hover:bg-muted/40"
                            data-testid="kb-next-link"
                        >
                            <div className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                                Next
                                <ChevronRight className="h-3.5 w-3.5" />
                            </div>
                            <div className="text-sm font-medium mt-1 group-hover:text-primary truncate">
                                {next.title}
                            </div>
                        </Link>
                    ) : (
                        <span className="flex-1" />
                    )}
                </nav>
            )}
        </article>
    );
}
